import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { BaseApiService } from './base-api.service';
import { Product } from '../models/product';
import { Role } from '../models/role';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseApiService<Product> {
  private http = inject(HttpClient);
  private logger = inject(NGXLogger);

  // Signal-based state management
  private readonly _products = signal<Product[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private _initialized = false;

  // Public readonly signals
  readonly products = this._products.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed signals
  readonly activeProducts = computed(() =>
    this.products().filter((product) => product.isActive),
  );

  readonly allRoles = computed(() =>
    this.activeProducts().flatMap((product) => product.roles),
  );

  constructor() {
    super('products');
  }

  initializeProducts(): Observable<Product[]> {
    if (this._initialized && this._products().length > 0) {
      return of(this._products());
    }

    this._initialized = true;
    this._loading.set(true);
    this._error.set(null);

    return this.getAllProducts().pipe(
      tap((products) => {
        this._products.set(products);
        this._loading.set(false);
      }),
      catchError((error) => {
        this._error.set('Failed to load products');
        this._loading.set(false);
        this.logger.error('Error loading products:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get all products from the API (legacy method)
   */
  getAllProducts(): Observable<Product[]> {
    const url = `${environment.apiHost}/${this.defaultVersion}/products`;
    return this.http.get<Product[]>(url);
  }

  /**
   * Filter user roles based on available roles
   */
  filterByAvailableRoles(userRoles: Role[], availableRoles: Role[]): Role[] {
    if (availableRoles.length === 0) {
      return [];
    }

    const availableRoleIds = new Set(availableRoles.map((role) => role.id));
    return userRoles.filter((role) => availableRoleIds.has(role.id));
  }

  /**
   * Format user roles for display
   */
  formatRoles(roles: Role[]): string {
    const availableRoles = this.allRoles();
    const filteredRoles = this.filterByAvailableRoles(roles, availableRoles);
    return filteredRoles.length === 0
      ? 'No roles assigned'
      : filteredRoles.map((role) => role.name).join(', ');
  }
}
